import styled, { createGlobalStyle, keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,229,160, .35); }
  50%      { box-shadow: 0 0 0 12px rgba(0,229,160, 0); }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

export const GlobalSepStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
`

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  min-height:100vh; display:flex; flex-direction:column;
  @media (max-width: 900px) { height:auto; overflow:visible; }
  @media (min-width: 901px) { height:100vh; overflow:hidden; }
`

export const Topbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:10px 16px; min-height:48px;
  display:flex; align-items:center; gap:14px; flex-shrink:0; flex-wrap:wrap;
  h2 { font-size:13px; font-weight:600; color:${c.txt}; }
  .crumb { font-size:11px; color:${c.txt3}; }
  @media (max-width: 600px) { padding:10px 12px; .crumb { display:none; } }
`

export const Body = styled.div`
  flex:1; display:grid; grid-template-columns: 1fr 380px;
  min-height:0;
  @media (max-width: 900px) { grid-template-columns: 1fr; overflow:visible; }
  @media (min-width: 901px) { overflow:hidden; }
`

// ─── Coluna principal: scan inputs ─────────────────────────────────────────
export const MainCol = styled.div`
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:30px 30px; overflow-y:auto; gap:14px;
  background: radial-gradient(circle at center 35%, ${c.bg2}, ${c.bg} 70%);
  @media (max-width: 900px) { padding:20px 12px; justify-content:flex-start; }
`

export const StepsCard = styled.div`
  width:100%; max-width:580px;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  padding:26px 28px; text-align:center;
  box-shadow:0 12px 40px rgba(0,0,0,.4);
  animation:${pulse} 2.4s ease-in-out infinite;
  @media (max-width: 600px) { padding:20px 16px; }
`

export const StepHeader = styled.div`
  margin-bottom:18px;
  .step-num {
    display:inline-flex; align-items:center; justify-content:center;
    width:32px; height:32px; border-radius:50%;
    background:${c.acc}; color:#000; font-weight:700; font-size:14px;
    font-family:${c.mono}; margin-bottom:8px;
  }
  h3 { font-size:18px; font-weight:600; color:${c.txt}; margin-bottom:4px; }
  p  { font-size:12px; color:${c.txt3}; line-height:1.5; }
`

export const StepInput = styled.input`
  width:100%; background:${c.bg3};
  border:2px solid ${({ $kind }) => $kind === 'err' ? c.danger : $kind === 'ok' ? c.acc : c.bdr2};
  border-radius:10px; padding:16px 18px;
  color:${c.txt}; font-size:18px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${c.acc}; box-shadow:0 0 0 4px ${c.adim}; }
  &::placeholder { color:${c.txt3}; letter-spacing:.02em; }
  &:disabled { opacity:.6; }
`

export const PairPreview = styled.div`
  margin-top:14px; padding:12px 14px;
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:9px;
  display:flex; justify-content:space-between; gap:10px; align-items:center;
  font-size:11px;
  .lbl { color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; font-size:10px; }
  .val { font-family:${c.mono}; font-size:13px; font-weight:700; color:${c.acc}; margin-top:2px; }
  .arrow { color:${c.txt3}; font-size:18px; }
`

export const Feedback = styled.div`
  margin-top:14px; min-height:20px;
  font-size:13px; font-weight:500;
  color:${({ $kind }) => $kind === 'ok' ? c.acc : $kind === 'err' ? c.danger : c.txt3};
`

export const ResetBtn = styled.button`
  margin-top:10px;
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt3};
  padding:8px 14px; border-radius:7px; cursor:pointer;
  font-size:11px; font-family:${c.sans};
  transition:.12s;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`

// ─── Coluna lateral: histórico ─────────────────────────────────────────────
export const SideCol = styled.aside`
  background:${c.bg2}; border-left:1px solid ${c.bdr};
  display:flex; flex-direction:column; overflow:hidden;
  @media (max-width: 900px) { border-left:none; border-top:1px solid ${c.bdr}; }
`
export const SideHeader = styled.div`
  padding:14px 18px; border-bottom:1px solid ${c.bdr};
  h3 { font-size:13px; font-weight:600; color:${c.txt}; margin-bottom:3px; }
  p { font-size:11px; color:${c.txt3}; }
`
export const SessionStats = styled.div`
  padding:12px 18px; border-bottom:1px solid ${c.bdr};
  display:grid; grid-template-columns:1fr 1fr; gap:10px;
  background:${c.bg3};
`
export const SessionStat = styled.div`
  .l { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; }
  .v {
    font-family:${c.mono}; font-size:18px; font-weight:700;
    color:${({ $variant }) => $variant === 'ok' ? c.acc : $variant === 'err' ? c.danger : c.txt};
  }
`
export const HistoryList = styled.div`
  flex:1; overflow-y:auto; padding:8px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
  @media (max-width: 900px) { max-height:300px; }
`
export const HistoryEmpty = styled.div`
  padding:30px 20px; text-align:center; color:${c.txt3}; font-size:11px;
  line-height:1.6;
`
export const HistoryItem = styled.div`
  background:${c.bg3}; border:1px solid ${c.bdr};
  border-left:3px solid ${({ $kind }) => $kind === 'ok' ? c.acc : c.danger};
  border-radius:8px; padding:9px 12px; margin-bottom:5px;
  animation:${fadeIn} .15s ease;
  display:flex; gap:10px; align-items:center;
  .icon { font-size:14px; flex-shrink:0;
          color:${({ $kind }) => $kind === 'ok' ? c.acc : c.danger}; }
  .info { flex:1; min-width:0; }
  .rastreio { font-family:${c.mono}; font-size:11px; font-weight:700; color:${c.txt}; }
  .meta { font-size:10px; color:${c.txt2}; margin-top:1px;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .time { font-family:${c.mono}; font-size:9px; color:${c.txt3}; flex-shrink:0; }
`

// ─── Modal de alerta wrong-box (volume errado) ─────────────────────────────
export const WrongBoxModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.7); backdrop-filter:blur(3px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center;
  padding:20px;
`
export const WrongBoxModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.danger}; border-top:6px solid ${c.danger};
  border-radius:14px; padding:30px 32px;
  width:560px; max-width:calc(100vw - 20px);
  box-shadow:0 16px 48px rgba(239,68,68,.4);
  text-align:center;
  animation:${fadeIn} .15s ease;
  .icon { font-size:64px; margin-bottom:10px; }
  h2 { font-size:20px; font-weight:700; color:${c.danger}; margin-bottom:10px; }
  .err-msg { font-size:14px; color:${c.txt}; line-height:1.5; margin-bottom:18px; }
  .correct-box {
    background:rgba(0,229,160,.10); border:1px solid ${c.acc2}; border-radius:10px;
    padding:14px 16px; margin-bottom:18px; text-align:left;
    .lbl { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; }
    .name { font-family:${c.mono}; font-size:18px; font-weight:700; color:${c.acc}; margin-top:3px; }
  }
`
export const WrongBoxBtn = styled.button`
  padding:11px 22px; border-radius:8px; cursor:pointer;
  font-size:13px; font-weight:700; font-family:${c.sans};
  background:${c.acc}; border:1px solid ${c.acc}; color:#000;
  transition:.12s;
  &:hover { filter:brightness(1.1); }
  @media (max-width: 600px) { width:100%; padding:14px; }
`
